import type { DeploymentCard } from '@central/api';
import {
  UptimeStrip,
  color,
  formatAgo,
  radius,
  formatPercent,
  InfoRow,
  HealthChip,
  StatusChip,
} from '@central/ui';
import { Link } from '@tanstack/react-router';
import { healthLabel } from '../format';
import { Mono } from './Mono';
import { StripEnds } from './StripLegend';

/**
 * One deployment tile: health, version when visible (with the newer release
 * the customer's maintenance covers, when there is one), 30-day uptime, a
 * note and the strip.
 */
export function DeploymentCardView({ d }: { d: DeploymentCard }) {
  return (
    <Link
      to="/deployments/$deploymentId"
      params={{ deploymentId: d.id }}
      aria-label={d.name}
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius.card,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        color: 'inherit',
        textDecoration: 'none',
        minWidth: 0,
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
      >
        <b style={{ fontSize: 17 }}>{d.name}</b>
        <HealthChip state={d.health} labels={healthLabel} />
      </div>
      <div>
        {d.kind === 'shared' && <InfoRow label="Hosting">Shared, run by Rethink</InfoRow>}
        {d.version !== undefined && (
          <InfoRow label="Version">
            <span
              style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Mono>{d.version}</Mono>
              {d.upgradeAvailable && (
                <StatusChip tone="blu">Upgrade available · {d.upgradeAvailable}</StatusChip>
              )}
            </span>
          </InfoRow>
        )}
        <InfoRow label="Uptime · 30 d">
          <Mono>{formatPercent(d.uptime30d)}</Mono>
        </InfoRow>
        <InfoRow label="Last report">
          {d.lastHeartbeatAt ? formatAgo(d.lastHeartbeatAt) : 'Never'}
        </InfoRow>
        {d.agents && (
          <InfoRow label="Communication agents">
            <Mono>
              {d.agents.connected} of {d.agents.total} connected
            </Mono>
          </InfoRow>
        )}
        {d.note && <InfoRow label="Note">{d.note}</InfoRow>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <UptimeStrip days={d.days} />
        <StripEnds days={d.days.length} />
      </div>
    </Link>
  );
}

/** Responsive grid of deployment tiles. */
export function DeploymentGrid({ items }: { items: DeploymentCard[] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
        gap: 16,
      }}
    >
      {items.map((d) => (
        <DeploymentCardView key={d.id} d={d} />
      ))}
    </div>
  );
}
